/**
 * Meal Plan React Query Hooks
 *
 * With Better Auth, session cookies are sent automatically.
 * No token passing needed - just call the API functions directly.
 */

import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useMemo, useRef } from "react";
import * as mealPlanApi from "../api/meal-plans";
import type { RescheduleMealRequest } from "../api/meal-plans";
import { track } from "../analytics";
import { calendarDateOf } from "../dates";
import { extractErrorCode, extractErrorStatus } from "../errors";
import { ApiError } from "../api/client";
import { useRecommendationFeedback } from "../providers/recommendation-feedback-provider";
import { RecommendationAttributionFlow, type RecommendationRequestMetadata, type RecommendationAcceptanceMetadata } from "../recommendation-attribution";
import { analyticsFailure } from "../analytics-failures";
import { GROCERY_LISTS_QUERY_KEY, MEAL_PLANS_QUERY_KEY } from "./query-keys";
import {
  reconcileAuthoritativeGroceryList,
  reconcileAuthoritativeMealPlan,
  reconcileAuthoritativeMealPlans,
} from "./use-grocery-lists";
import type {
  CreateMealPlanRequest,
  SwapMealRequest,
  AddMealRequest,
  MealPlan,
  MealType,
  MealSlot,
  SuggestMealRequest,
  PreviewSuggestedMealRequest,
  PreviewMealPlanRequest,
  CommitMealPlanRequest,
} from "../api/types";

// A3 re-review F5: this drove the plan_generated analytics event's
// mealTypes list, and left "snacks" out even though createMealPlanSchema
// (validators) and GeneratePlanModal have supported it since #371/the A3
// fix round -- a plan generated with only Snacks selected (or Snacks
// alongside others) would report an incomplete/misleading mealTypes list.
const INCLUDED_MEAL_TYPES = ["breakfast", "lunch", "dinner", "snacks"] as const;
const DAYS_PER_PLAN = 7;

/** Meal types the request asked for, in a fixed, stable order. */
function requestedMealTypes(
  includedMeals: CreateMealPlanRequest["includedMeals"],
): Array<"breakfast" | "lunch" | "dinner" | "snacks"> {
  return INCLUDED_MEAL_TYPES.filter((mealType) => includedMeals[mealType]);
}

function requestedSlotCount(
  includedMeals: CreateMealPlanRequest["includedMeals"],
  dinnerNights: 3 | 5 | 7,
): number {
  return requestedMealTypes(includedMeals).reduce(
    (total, mealType) =>
      total + (mealType === "dinner" ? dinnerNights : DAYS_PER_PLAN),
    0,
  );
}

export { MEAL_PLANS_QUERY_KEY };

/**
 * Fetch all meal plans.
 *
 * `options.enabled` defaults to true (every pre-existing zero-arg call
 * site is unaffected). AddToPlanControl (#446) passes `enabled: false` for
 * an anonymous visitor -- e.g. someone who opened a shared /recipes/[id]
 * link (Task 1, #449) while logged out -- so this authenticated-only query
 * never fires and 401s in the background.
 */
export function useMealPlans(options?: { enabled?: boolean }) {
  const queryClient = useQueryClient();
  return useQuery({
    queryKey: [MEAL_PLANS_QUERY_KEY],
    queryFn: async ({ signal }) => {
      const incoming = await mealPlanApi.getMealPlans();
      return signal.aborted ? incoming : reconcileAuthoritativeMealPlans(queryClient, incoming);
    },
    enabled: options?.enabled,
  });
}

/**
 * Fetch a single meal plan
 */
export function useMealPlan(id: string) {
  const queryClient = useQueryClient();
  return useQuery({
    queryKey: [MEAL_PLANS_QUERY_KEY, id],
    queryFn: async ({ signal }) => {
      const incoming = await mealPlanApi.getMealPlan(id);
      return signal.aborted ? incoming : reconcileAuthoritativeMealPlan(queryClient, incoming);
    },
    enabled: !!id,
  });
}

export interface PreviewMealPlanVariables {
  input: PreviewMealPlanRequest;
  signal?: AbortSignal;
  attribution?: RecommendationRequestMetadata;
}

/**
 * Compute an initial menu, refresh, or Review. Preview is deliberately a
 * mutation because it is a POST computation, but it is read-only: it never
 * updates or invalidates any durable query cache.
 */
export function usePreviewMealPlan() {
  return useMutation({
    mutationFn: ({ input, signal, attribution }: PreviewMealPlanVariables) =>
      attribution ? mealPlanApi.previewMealPlan(input, signal, attribution) : mealPlanApi.previewMealPlan(input, signal),
    retry: false,
  });
}

function shouldRetryCandidateCommit(
  failureCount: number,
  error: unknown,
): boolean {
  if (failureCount >= 1) return false;
  // A COMMIT_TIMED_OUT means the database already exceeded its commit
  // budget. Auto-retrying with retryDelay: 0 immediately resends into an
  // already-slow database and makes the user wait twice for no benefit --
  // GeneratePlanModal's manual "Try again" is what's actually safe here,
  // since it replays the identical commitId against the server's
  // idempotency receipt. Never auto-retry this code, even though its 503
  // status would otherwise qualify below.
  if (extractErrorCode(error) === "COMMIT_TIMED_OUT") {
    return false;
  }
  const status = extractErrorStatus(error);
  return status === 0 || (status >= 500 && status <= 599);
}

function retainGroceryProjection(
  current: MealPlan | undefined,
  committed: MealPlan,
): MealPlan {
  return current?.groceryList
    ? { ...committed, groceryList: current.groceryList }
    : committed;
}

function upsertCommittedPlan(plans: MealPlan[], committed: MealPlan): MealPlan[] {
  const index = plans.findIndex((plan) => plan.id === committed.id);
  if (index < 0) return [committed, ...plans];
  return plans.map((plan) =>
    plan.id === committed.id
      ? retainGroceryProjection(plan, committed)
      : plan,
  );
}

/**
 * Commit an already reviewed candidate selection. The mutation retries one
 * transport/server failure with the exact caller-owned request object so the
 * same commit UUID reaches the server's idempotency boundary.
 */
export type CommitMealPlanVariables = CommitMealPlanRequest & { attribution?: RecommendationAcceptanceMetadata; expectedActorUserId?: string };

export function useCommitMealPlan() {
  const queryClient = useQueryClient();
  const feedback = useRecommendationFeedback();
  const currentActor = (expectedActorUserId?: string) => feedback?.isCurrentActor?.() !== false &&
    (!expectedActorUserId || feedback?.actorUserId === expectedActorUserId);
  const initiatingActor = (request: CommitMealPlanVariables) => request.expectedActorUserId ?? request.attribution?.expectedActorUserId ?? feedback?.actorUserId;
  const operations = useRef(new WeakMap<CommitMealPlanVariables, { actorUserId?: string; request: CommitMealPlanRequest }>());
  const captureOperation = (variables: CommitMealPlanVariables) => {
    const { attribution, expectedActorUserId, ...body } = variables;
    const operation = { actorUserId: initiatingActor(variables), request: attribution || expectedActorUserId ? body : variables };
    operations.current.set(variables, operation);
    return operation;
  };

  return useMutation({
    onMutate: captureOperation,
    mutationFn: (variables: CommitMealPlanVariables) => {
      const { attribution } = variables;
      const { actorUserId: actor, request } = operations.current.get(variables) ?? captureOperation(variables);
      if (!currentActor(actor) || (attribution && attribution.expectedActorUserId !== actor)) throw new ApiError("Signed-in account changed", 409);
      return actor ? mealPlanApi.commitMealPlan(request, attribution, actor) : mealPlanApi.commitMealPlan(request);
    },
    retry: shouldRetryCandidateCommit,
    retryDelay: 0,
    onSuccess: async ({ mealPlan, groceryList }, request, context) => {
      if (!currentActor(context?.actorUserId ?? initiatingActor(request))) return;
      queryClient.setQueryData(
        [MEAL_PLANS_QUERY_KEY, mealPlan.id],
        (current: MealPlan | undefined) =>
          retainGroceryProjection(current, mealPlan),
      );

      // A cold list cache means "not loaded", not "the user has no plans".
      // Only patch an existing root list; the awaited exact invalidation below
      // remains responsible for loading it when a consumer is active.
      if (
        queryClient.getQueryData<MealPlan[]>([MEAL_PLANS_QUERY_KEY]) !==
        undefined
      ) {
        queryClient.setQueryData<MealPlan[]>(
          [MEAL_PLANS_QUERY_KEY],
          (plans) => (plans ? upsertCommittedPlan(plans, mealPlan) : plans),
        );
      }

      const aisleTargetKey = [
        GROCERY_LISTS_QUERY_KEY,
        "meal-plan",
        mealPlan.id,
        "aisle",
      ] as const;
      const reconciledGroceryList = reconcileAuthoritativeGroceryList(
        queryClient,
        groceryList,
        aisleTargetKey,
      );
      // The commit response is the server's default aisle projection. The
      // reconciler safely fans its causal state into warm projections, while
      // this write installs a cold aisle target with the reconciled value.
      queryClient.setQueryData(aisleTargetKey, reconciledGroceryList);

      await Promise.all([
        queryClient.invalidateQueries({
          queryKey: [MEAL_PLANS_QUERY_KEY],
          exact: true,
        }),
        queryClient.invalidateQueries({
          queryKey: [GROCERY_LISTS_QUERY_KEY, "meal-plan", mealPlan.id],
        }),
      ]);
    },
  });
}

/**
 * Generate a new meal plan
 */
export function useGenerateMealPlan(options?: { dinnerNights?: 3 | 5 | 7 }) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (request: CreateMealPlanRequest) =>
      mealPlanApi.generateMealPlan(request),
    onMutate: (request) => {
      const previousPlans = queryClient.getQueryData<MealPlan[]>([
        MEAL_PLANS_QUERY_KEY,
      ]);
      // A cold cache (query never fetched — undefined, not []) is NOT proof
      // the user has no plans; it just means we don't know yet. Treat that
      // as isFirstPlan: false rather than inflate the activation metric.
      const isFirstPlan =
        previousPlans !== undefined && previousPlans.length === 0;
      const mealTypes = requestedMealTypes(request.includedMeals);
      const expectedSlotCount = requestedSlotCount(
        request.includedMeals,
        options?.dinnerNights ?? 7,
      );

      track("plan_generation_started", {
        mealTypeCount: mealTypes.length,
        requestedSlotCount: expectedSlotCount,
        isFirstPlan,
      });

      return { isFirstPlan, expectedSlotCount };
    },
    onSuccess: async (data, request, context) => {
      const isFirstPlan = context?.isFirstPlan ?? false;

      const mealTypes = requestedMealTypes(request.includedMeals);
      const expectedSlotCount =
        context?.expectedSlotCount ??
        requestedSlotCount(request.includedMeals, options?.dinnerNights ?? 7);
      const slotCount = data.mealSlots.length;

      track("plan_generated", {
        mealTypes,
        slotCount,
        emptySlotCount: Math.max(0, expectedSlotCount - slotCount),
        isFirstPlan,
      });

      // Invalidate meal plans and AWAIT the refetch (#172-1). Callers
      // (App.tsx's handlePlanGenerated, MealPlanner.tsx's handleGenerate)
      // navigate to the generated week as soon as
      // generateMutation.mutateAsync(...) resolves. mutateAsync doesn't
      // settle until this onSuccess's returned promise settles -- so
      // awaiting invalidateQueries here means that navigation only happens
      // once the meal-plans list cache actually contains the new plan.
      // Without the await, invalidateQueries fires the refetch but doesn't
      // block on it, and the navigation would land on the generated week
      // before the refetch lands -- an empty-week flash under the
      // "generated!" toast until the background refetch finishes.
      await queryClient.invalidateQueries({ queryKey: [MEAL_PLANS_QUERY_KEY] });
    },
    // Closed status/reason only: the API's message can echo the user's own
    // preferences (allergy/dislike names) back, and analytics must stay free
    // of PII.
    onError: (error) => {
      track("plan_generation_failed", analyticsFailure(error, "plan"));
    },
  });
}

/**
 * Swap a meal in a meal plan
 */
export type SwapMealVariables = SwapMealRequest & { attribution?: RecommendationAcceptanceMetadata; expectedActorUserId?: string; practicalFitRevision?: string };
export function useSwapMeal(mealPlanId: string) {
  const queryClient = useQueryClient();
  const feedback = useRecommendationFeedback();
  const currentActor = (actor?: string) => feedback?.isCurrentActor?.() !== false && (!actor || actor === feedback?.actorUserId);
  const operations = useRef(new WeakMap<SwapMealVariables, { actorUserId?: string; mealPlanId: string; request: SwapMealRequest; practicalFitRevision?: string }>());
  const capture = (variables: SwapMealVariables) => {
    const { attribution, expectedActorUserId, practicalFitRevision, ...body } = variables;
    const operation = { actorUserId: expectedActorUserId ?? attribution?.expectedActorUserId ?? feedback?.actorUserId, mealPlanId,
      practicalFitRevision, request: attribution || expectedActorUserId || practicalFitRevision ? body : variables };
    operations.current.set(variables, operation);
    return operation;
  };
  return useMutation({
    onMutate: capture,
    mutationFn: (variables: SwapMealVariables) => {
      const operation = operations.current.get(variables) ?? capture(variables);
      if (!currentActor(operation.actorUserId) || (variables.attribution && variables.attribution.expectedActorUserId !== operation.actorUserId)) throw new ApiError("Signed-in account changed", 409);
      return operation.practicalFitRevision ? mealPlanApi.swapMeal(operation.mealPlanId, operation.request, variables.attribution, operation.actorUserId, operation.practicalFitRevision)
        : operation.actorUserId ? mealPlanApi.swapMeal(operation.mealPlanId, operation.request, variables.attribution, operation.actorUserId) : mealPlanApi.swapMeal(operation.mealPlanId, operation.request);
    },
    onSuccess: async (data, _variables, context) => {
      if (!currentActor(context?.actorUserId)) return;
      track("meal_swapped", { mealType: data.mealType });
      const targetPlanId = context!.mealPlanId;
      patchPlanSlotCaches(queryClient, targetPlanId, data);
      await settlePlanMutationCaches(queryClient, targetPlanId);
    },
  });
}

/**
 * Add a new meal to a meal plan
 */
export function useAddMeal() {
  const queryClient = useQueryClient();

  return useMutation({
    // Picker dismissal can change render-bound options before this runs.
    // Carry the selected plan with the mutation, including its cache refresh.
    mutationFn: (
      { mealPlanId, ...request }: AddMealRequest & { mealPlanId: string },
    ) =>
      mealPlanApi.addMeal(mealPlanId, request),
    onSuccess: async (slot, { mealPlanId }) => {
      patchPlanSlotCaches(queryClient, mealPlanId, slot, appendSuggestedSlot);
      await settlePlanMutationCaches(queryClient, mealPlanId);
    },
  });
}

export function isSuggestMealSlotOccupiedError(error: unknown): boolean {
  if (!(error instanceof ApiError) || error.status !== 409) return false;
  const data = error.data as
    | { details?: { code?: string } }
    | undefined;
  return data?.details?.code === "SLOT_OCCUPIED";
}

function appendSuggestedSlot(plan: MealPlan, slot: MealSlot): MealPlan {
  if (plan.id !== slot.mealPlanId) return plan;
  if (plan.mealSlots.some((existing) => existing.id === slot.id)) return plan;
  return { ...plan, mealSlots: [...plan.mealSlots, slot] };
}

function replaceSlot(plan: MealPlan, slot: MealSlot): MealPlan {
  if (plan.id !== slot.mealPlanId) return plan;
  if (!plan.mealSlots.some((existing) => existing.id === slot.id)) return plan;
  return {
    ...plan,
    mealSlots: plan.mealSlots.map((existing) =>
      existing.id === slot.id ? slot : existing,
    ),
  };
}

function removeSlot(plan: MealPlan, mealPlanId: string, slotId: string): MealPlan {
  if (plan.id !== mealPlanId) return plan;
  if (!plan.mealSlots.some((slot) => slot.id === slotId)) return plan;
  return { ...plan, mealSlots: plan.mealSlots.filter((slot) => slot.id !== slotId) };
}

function patchPlanSlotCaches(
  queryClient: ReturnType<typeof useQueryClient>,
  mealPlanId: string,
  slot: MealSlot,
  patch: (plan: MealPlan, slot: MealSlot) => MealPlan = replaceSlot,
): void {
  queryClient.setQueryData<MealPlan[]>([MEAL_PLANS_QUERY_KEY], (plans) =>
    plans?.map((plan) =>
      plan.id === mealPlanId ? patch(plan, slot) : plan,
    ),
  );
  queryClient.setQueryData<MealPlan>(
    [MEAL_PLANS_QUERY_KEY, mealPlanId],
    (plan) => (plan?.id === mealPlanId ? patch(plan, slot) : plan),
  );
}

function removePlanSlotCaches(
  queryClient: ReturnType<typeof useQueryClient>,
  mealPlanId: string,
  slotId: string,
): void {
  queryClient.setQueryData<MealPlan[]>([MEAL_PLANS_QUERY_KEY], (plans) =>
    plans?.map((plan) => removeSlot(plan, mealPlanId, slotId)),
  );
  queryClient.setQueryData<MealPlan>(
    [MEAL_PLANS_QUERY_KEY, mealPlanId],
    (plan) => (plan ? removeSlot(plan, mealPlanId, slotId) : plan),
  );
}

function settlePlanMutationCaches(
  queryClient: ReturnType<typeof useQueryClient>,
  mealPlanId: string,
): Promise<unknown[]> {
  return Promise.all([
    queryClient.invalidateQueries({
      queryKey: [MEAL_PLANS_QUERY_KEY],
      exact: true,
    }),
    queryClient.invalidateQueries({
      queryKey: [MEAL_PLANS_QUERY_KEY, mealPlanId],
      exact: true,
    }),
    queryClient.invalidateQueries({
      queryKey: [GROCERY_LISTS_QUERY_KEY, "meal-plan", mealPlanId],
    }),
  ]);
}

/**
 * Add one server-selected recipe to an explicit empty slot. An honest
 * no-match is read-only. Successful writes patch both plan caches before
 * refetching, and occupied races refetch plan state without touching the
 * grocery cache because the server performed no write.
 */
export type PreviewSuggestedMealVariables = PreviewSuggestedMealRequest & { attribution?: RecommendationRequestMetadata; expectedActorUserId?: string };
export function usePreviewSuggestedMeal(mealPlanId: string) {
  const queryClient = useQueryClient();
  const feedback = useRecommendationFeedback();
  const currentActor = (variables: PreviewSuggestedMealVariables) => feedback?.isCurrentActor?.() !== false &&
    (!(variables.expectedActorUserId ?? variables.attribution?.expectedActorUserId) || feedback?.actorUserId === (variables.expectedActorUserId ?? variables.attribution?.expectedActorUserId));
  return useMutation({
    mutationFn: (variables: PreviewSuggestedMealVariables) => {
      if (!currentActor(variables)) throw new ApiError("Signed-in account changed", 409);
      const { attribution, expectedActorUserId, ...request } = variables;
      return attribution || expectedActorUserId ? mealPlanApi.previewSuggestedMeal(mealPlanId, request, attribution, expectedActorUserId) : mealPlanApi.previewSuggestedMeal(mealPlanId, variables);
    },
    onError: (error, variables) => {
      if (!currentActor(variables)) return;
      if (!isSuggestMealSlotOccupiedError(error)) return;
      void queryClient.invalidateQueries({ queryKey: [MEAL_PLANS_QUERY_KEY, mealPlanId], exact: true });
      void queryClient.invalidateQueries({ queryKey: [MEAL_PLANS_QUERY_KEY], exact: true });
    },
  });
}

export function useSuggestMeal(mealPlanId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (request: SuggestMealRequest) =>
      mealPlanApi.suggestMeal(mealPlanId, request),
    onSuccess: async (response, request) => {
      track("single_meal_suggestion_completed", {
        outcome: response.outcome === "added" ? "added" : "no_match",
        mealType: request.mealType,
        hasCuisineScope: Boolean(request.cuisine),
      });
      if (response.outcome !== "added") return;

      queryClient.setQueryData<MealPlan[]>([MEAL_PLANS_QUERY_KEY], (plans) =>
        plans?.map((plan) => appendSuggestedSlot(plan, response.slot)),
      );
      queryClient.setQueryData<MealPlan>(
        [MEAL_PLANS_QUERY_KEY, mealPlanId],
        (plan) => (plan ? appendSuggestedSlot(plan, response.slot) : plan),
      );
      await settlePlanMutationCaches(queryClient, mealPlanId);
    },
    onError: (error, request) => {
      if (!isSuggestMealSlotOccupiedError(error)) return;
      track("single_meal_suggestion_completed", {
        outcome: "occupied",
        mealType: request.mealType,
        hasCuisineScope: Boolean(request.cuisine),
      });
      queryClient.invalidateQueries({
        queryKey: [MEAL_PLANS_QUERY_KEY, mealPlanId],
        exact: true,
      });
      queryClient.invalidateQueries({
        queryKey: [MEAL_PLANS_QUERY_KEY],
        exact: true,
      });
    },
  });
}

/**
 * Remove a single meal (slot) from a meal plan.
 */
export function useRemoveMeal(mealPlanId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (slotId: string) => mealPlanApi.removeMeal(mealPlanId, slotId),
    onSuccess: async (_data, slotId) => {
      removePlanSlotCaches(queryClient, mealPlanId, slotId);
      await settlePlanMutationCaches(queryClient, mealPlanId);
    },
  });
}

/**
 * Pure cache patch mirroring the server's reschedule semantics (ladder item
 * 12): move the slot to (date, mealType); if another slot already occupies
 * that position, the two SWAP (the occupant takes the moved slot's original
 * position). Exported for direct unit testing.
 *
 * Dates: `date` is a calendar date ("YYYY-MM-DD"); slot dates are @db.Date
 * serializations ("YYYY-MM-DDT00:00:00.000Z") -- compared via
 * calendarDateOf per lib/dates' string-comparison rule, and written back in
 * the serialized shape so the patched cache is indistinguishable from a
 * fresh fetch.
 */
export function applyRescheduleToPlan(
  plan: MealPlan,
  slotId: string,
  date: string,
  mealType?: MealType,
): MealPlan {
  const moved = plan.mealSlots.find((s) => s.id === slotId);
  if (!moved) return plan;

  const targetMealType = mealType ?? moved.mealType;
  const targetDate = `${date}T00:00:00.000Z`;
  const occupant = plan.mealSlots.find(
    (s) =>
      s.id !== slotId &&
      calendarDateOf(s.date) === date &&
      s.mealType === targetMealType,
  );

  return {
    ...plan,
    mealSlots: plan.mealSlots.map((s) => {
      if (s.id === moved.id) {
        return { ...s, date: targetDate, mealType: targetMealType };
      }
      if (occupant && s.id === occupant.id) {
        return { ...s, date: moved.date, mealType: moved.mealType };
      }
      return s;
    }),
  };
}

/**
 * Reschedule a meal (slot) to another day within the same plan (ladder item
 * 12). Optimistic with rollback (house pattern: useToggleFavorite): both
 * the plans-list cache (MealPlanner reads the LIST endpoint) and the
 * plan-detail cache are patched via applyRescheduleToPlan, snapshotted, and
 * restored on error; onSettled re-syncs both from the server.
 *
 * The grocery list query is deliberately NOT invalidated -- unlike
 * swap/add/remove, the API never refreshes the list on a reschedule (same
 * recipes, same servings; consolidation is date-independent), so the cached
 * list is still correct and an invalidation would only cost a refetch.
 *
 * NOTE: no analytics event is emitted -- `meal_rescheduled` is not yet in
 * the analytics taxonomy (lib/analytics.ts is owned by a parallel change);
 * wire it up as a follow-up once the event exists.
 */
export function useRescheduleMeal(mealPlanId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      slotId,
      ...request
    }: { slotId: string } & RescheduleMealRequest) =>
      mealPlanApi.rescheduleMeal(mealPlanId, slotId, request),
    onMutate: async ({ slotId, date, mealType }) => {
      // Prefix match cancels the list AND every plan-detail refetch.
      await queryClient.cancelQueries({ queryKey: [MEAL_PLANS_QUERY_KEY] });

      const previousList = queryClient.getQueryData<MealPlan[]>([
        MEAL_PLANS_QUERY_KEY,
      ]);
      const previousPlan = queryClient.getQueryData<MealPlan>([
        MEAL_PLANS_QUERY_KEY,
        mealPlanId,
      ]);

      queryClient.setQueryData<MealPlan[]>([MEAL_PLANS_QUERY_KEY], (old) =>
        old?.map((plan) =>
          plan.id === mealPlanId
            ? applyRescheduleToPlan(plan, slotId, date, mealType)
            : plan,
        ),
      );
      queryClient.setQueryData<MealPlan>(
        [MEAL_PLANS_QUERY_KEY, mealPlanId],
        (old) =>
          old ? applyRescheduleToPlan(old, slotId, date, mealType) : old,
      );

      return { previousList, previousPlan };
    },
    onError: (_err, _vars, context) => {
      if (context?.previousList !== undefined) {
        queryClient.setQueryData([MEAL_PLANS_QUERY_KEY], context.previousList);
      }
      if (context?.previousPlan !== undefined) {
        queryClient.setQueryData(
          [MEAL_PLANS_QUERY_KEY, mealPlanId],
          context.previousPlan,
        );
      }
    },
    onSettled: () => {
      queryClient.invalidateQueries({
        queryKey: [MEAL_PLANS_QUERY_KEY, mealPlanId],
      });
      queryClient.invalidateQueries({
        queryKey: [MEAL_PLANS_QUERY_KEY],
      });
    },
  });
}

function withSlotServings(
  plan: MealPlan,
  slotId: string,
  servings: number,
): MealPlan {
  return {
    ...plan,
    mealSlots: plan.mealSlots.map((slot) =>
      slot.id === slotId ? { ...slot, servings } : slot,
    ),
  };
}

/**
 * Change a planned meal's servings (issue #1201). Optimistic with rollback
 * (house pattern: useRescheduleMeal) on both plan caches. Unlike reschedule,
 * the server refreshes the grocery list -- quantities scale with servings --
 * so settling also invalidates that list.
 */
export function useUpdateMealServings(mealPlanId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ slotId, servings }: { slotId: string; servings: number }) =>
      mealPlanApi.updateMealServings(mealPlanId, slotId, servings),
    onMutate: async ({ slotId, servings }) => {
      await queryClient.cancelQueries({ queryKey: [MEAL_PLANS_QUERY_KEY] });
      const previousList = queryClient.getQueryData<MealPlan[]>([
        MEAL_PLANS_QUERY_KEY,
      ]);
      const previousPlan = queryClient.getQueryData<MealPlan>([
        MEAL_PLANS_QUERY_KEY,
        mealPlanId,
      ]);
      queryClient.setQueryData<MealPlan[]>([MEAL_PLANS_QUERY_KEY], (old) =>
        old?.map((plan) =>
          plan.id === mealPlanId
            ? withSlotServings(plan, slotId, servings)
            : plan,
        ),
      );
      queryClient.setQueryData<MealPlan>(
        [MEAL_PLANS_QUERY_KEY, mealPlanId],
        (old) => (old ? withSlotServings(old, slotId, servings) : old),
      );
      return { previousList, previousPlan };
    },
    onError: (_err, _vars, context) => {
      if (context?.previousList !== undefined) {
        queryClient.setQueryData([MEAL_PLANS_QUERY_KEY], context.previousList);
      }
      if (context?.previousPlan !== undefined) {
        queryClient.setQueryData(
          [MEAL_PLANS_QUERY_KEY, mealPlanId],
          context.previousPlan,
        );
      }
    },
    onSettled: () => settlePlanMutationCaches(queryClient, mealPlanId),
  });
}

/**
 * Delete a meal plan
 */
export function useDeleteMealPlan() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => mealPlanApi.deleteMealPlan(id),
    onSuccess: async (_data, deletedPlanId) => {
      // A confirmed delete is an identity transition, not merely stale data.
      // Stop in-flight reads that could repopulate the deleted plan/list,
      // then make every local consumer truthful before navigation resumes.
      await Promise.all([
        queryClient.cancelQueries({
          queryKey: [MEAL_PLANS_QUERY_KEY],
          exact: true,
        }),
        queryClient.cancelQueries({
          queryKey: [MEAL_PLANS_QUERY_KEY, deletedPlanId],
          exact: true,
        }),
        queryClient.cancelQueries({
          predicate: (query) =>
            isGroceryQueryForPlan(query.queryKey, deletedPlanId),
        }),
      ]);

      queryClient.setQueryData<MealPlan[] | undefined>(
        [MEAL_PLANS_QUERY_KEY],
        (plans) => plans?.filter((plan) => plan.id !== deletedPlanId),
      );
      queryClient.removeQueries({
        queryKey: [MEAL_PLANS_QUERY_KEY, deletedPlanId],
        exact: true,
      });
      queryClient.removeQueries({
        predicate: (query) =>
          isGroceryQueryForPlan(query.queryKey, deletedPlanId),
      });

      // Refetch is confirmation of the already-correct local transition. An
      // exact root match avoids disturbing unrelated plan-detail observers.
      await queryClient.invalidateQueries({
        queryKey: [MEAL_PLANS_QUERY_KEY],
        exact: true,
      });
    },
  });
}

function isGroceryQueryForPlan(
  queryKey: readonly unknown[],
  mealPlanId: string,
): boolean {
  return (
    queryKey[0] === GROCERY_LISTS_QUERY_KEY &&
    queryKey[1] === "meal-plan" &&
    queryKey[2] === mealPlanId
  );
}

/**
 * Reset a meal plan in place (Track C, Task 5, issue #448). Invalidation
 * mirrors useSwapMeal: the specific plan, the plans list, and the plan's
 * grocery list (the API refreshes it server-side as part of the same
 * reset, so the cached list must refetch too or it keeps showing the
 * wiped-out meals' ingredients).
 */
export function useResetMealPlan(mealPlanId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: () => mealPlanApi.resetMealPlan(mealPlanId),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: [MEAL_PLANS_QUERY_KEY, mealPlanId],
      });
      queryClient.invalidateQueries({
        queryKey: [MEAL_PLANS_QUERY_KEY],
      });
      queryClient.invalidateQueries({
        queryKey: [GROCERY_LISTS_QUERY_KEY, "meal-plan", mealPlanId],
      });
    },
  });
}

/**
 * Get swap suggestions for a meal slot.
 *
 * The `enabled: !!mealSlotId` gate is load-bearing for RecipePickerModal's
 * ADD mode: the modal calls this hook unconditionally (Rules of Hooks) with
 * `mealSlotId ?? ""`, relying on the gate to keep the query idle. Removing
 * it would fire GET /meal-plans/swap-suggestions/ with an empty id on every
 * add-meal open.
 */
export function useSwapSuggestions(mealSlotId: string, count?: number, options?: { enabled: boolean; mealType?: MealType }) {
  const feedback = useRecommendationFeedback();
  const session = useMemo(() => options?.enabled && options.mealType && feedback?.actorUserId
    ? { flow: new RecommendationAttributionFlow("swap", feedback.actorUserId), hasResponse: false, input: { mealSlotId, count: count ?? 5 } } : null,
  [options?.enabled, options?.mealType, feedback?.actorUserId, mealSlotId, count]);
  const suppressionKey = (feedback?.suppressions ?? []).filter(row => row.scope === "global" || row.scope === options?.mealType)
    .map(row => `${row.scope}:${row.recipeId}`).sort().join("|");
  // A restored policy may equal an earlier one; each transition still needs fresh evidence.
  const policy = useMemo(() => ({ signature: suppressionKey, revision: crypto.randomUUID() }), [suppressionKey]);
  const currentPolicy = useRef(policy.revision);
  currentPolicy.current = policy.revision;
  const query = useQuery({
    queryKey: ["swap-suggestions", mealSlotId, count, feedback?.actorUserId, ...(options ? [session?.flow.attemptId, policy.revision] : [])],
    queryFn: async ({ signal }) => {
      if (!session || !options?.mealType) {
        const response = await mealPlanApi.getSwapSuggestions(mealSlotId, count);
        return Array.isArray(response) ? response : response.recipes;
      }
      if (feedback?.isCurrentActor?.() === false) throw new ApiError("Signed-in account changed", 409);
      const metadata = session.flow.request({ ...session.input, policyRevision: policy.revision }, session.hasResponse ? "feedback_refresh" : "initial");
      try {
        const response = await mealPlanApi.getSwapSuggestions(mealSlotId, count, metadata, signal);
        if (feedback?.isCurrentActor?.() === false || currentPolicy.current !== policy.revision) throw new ApiError("Recommendation context changed", 409);
        const recipes = Array.isArray(response) ? response : response.recipes;
        session.flow.observeResponse(metadata, Array.isArray(response) ? undefined : response.attribution, [{ mealType: options.mealType, candidates: recipes }]);
        session.hasResponse = true;
        return recipes;
      } catch (error) { session.flow.rejectResponse(metadata, error); throw error; }
    },
    enabled: !!mealSlotId && (options?.enabled ?? true),
    ...(options ? { structuralSharing: false, staleTime: Infinity, refetchOnWindowFocus: false, refetchOnReconnect: false } : {}),
  });
  return { ...query, attributionFlow: session?.flow ?? null };
}
